import axios from 'axios';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL, RESTRICTION_TYPES, RESTRICTION_ACTIONS, PARAM_TYPES } from './constants';
import { getToken } from '../../services/tokenService';
export default function useParametresBaticoExtra(data) {
  const {
    ajustementDialogOpen, ajustementForm, ajustementsMontants, customParamDialogOpen, customParamForm, customParams,
    dialogOpen, editMode, editingAjustement, editingCustomParam, editingRestriction, expandedSections,
    exportToCSV, exportToExcel, exportToJSON, fetchAjustementsMontants, fetchCustomParams, fetchData,
    fetchTerritoires, fileInputRef, formData, handleArchive, handleCreateAjustement, handleDelete,
    handleDeleteAjustement, handleEdit, handleInputChange, handleSubmit, handleUpdateAjustement, historique,
    importData, importDialogOpen, loading, openEditAjustement, openNewDialog, parametresActifs,
    previewDialogOpen, previewParams, resetAjustementForm, restrictionDialogOpen, restrictionForm, restrictions,
    selectedParams, setAjustementDialogOpen, setAjustementForm, setAjustementsMontants, setCustomParamDialogOpen, setCustomParamForm,
    setCustomParams, setDialogOpen, setEditMode, setEditingAjustement, setEditingCustomParam, setEditingRestriction,
    setExpandedSections, setFormData, setHistorique, setImportData, setImportDialogOpen, setLoading,
    setParametresActifs, setPreviewDialogOpen, setPreviewParams, setRestrictionDialogOpen, setRestrictionForm, setRestrictions,
    setSelectedParams, setStats, setTerritoiresDisponibles, stats, territoiresDisponibles, toggleSection} = data;

  const openCustomParamDialog = (param = null) => {
    if (param) {
      setEditingCustomParam(param);
      setCustomParamForm({
        nom: param.nom,
        type: param.type,
        mode_calcul: param.mode_calcul,
        valeur: param.valeur,
        description: param.description || '',
        applicable_a: param.applicable_a || ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
        is_active: param.is_active !== false
      });
    } else {
      setEditingCustomParam(null);
      setCustomParamForm({
        nom: '',
        type: 'taxe',
        mode_calcul: 'pourcentage',
        valeur: 0,
        description: '',
        applicable_a: ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
        is_active: true
      });
    }
    setCustomParamDialogOpen(true);
  };

  const handleSaveCustomParam = async () => {
    if (!customParamForm.nom.trim()) {
      toast.error('Le nom du paramètre est obligatoire');
      return;
    }

    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      if (editingCustomParam) {
        await axios.put(
          `${BACKEND_URL}/api/parametres-batico/custom/${editingCustomParam.id}`,
          customParamForm,
          { headers }
        );
        toast.success('Paramètre mis à jour avec succès');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/parametres-batico/custom`,
          customParamForm,
          { headers }
        );
        toast.success('Nouveau paramètre créé avec succès');
      }

      setCustomParamDialogOpen(false);
      fetchCustomParams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCustomParam = async (paramId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paramètre ?')) {
      return;
    }

    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${BACKEND_URL}/api/parametres-batico/custom/${paramId}`, { headers });
      toast.success('Paramètre supprimé');
      fetchCustomParams();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleCustomParam = async (param) => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${BACKEND_URL}/api/parametres-batico/custom/${param.id}`,
        { ...param, is_active: !param.is_active },
        { headers }
      );
      toast.success(`Paramètre ${param.is_active ? 'désactivé' : 'activé'}`);
      fetchCustomParams();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleApplicableA = (category) => {
    setCustomParamForm(prev => {
      const current = prev.applicable_a || [];
      if (current.includes(category)) {
        return { ...prev, applicable_a: current.filter(c => c !== category) };
      } else {
        return { ...prev, applicable_a: [...current, category] };
      }
    });
  };

  const formatCustomParamValue = (param) => {
    switch (param.mode_calcul) {
      case 'pourcentage':
        return `${param.valeur}%`;
      case 'fixe':
        return `${param.valeur.toLocaleString('fr-FR')} €`;
      case 'coefficient':
        return `×${param.valeur}`;
      default:
        return param.valeur;
    }
  };

  const getParamTypeInfo = (type) => {
    return PARAM_TYPES.find(t => t.value === type) || PARAM_TYPES[PARAM_TYPES.length - 1];
  };

  // ==================== FONCTIONS RESTRICTIONS ====================

  const fetchRestrictions = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/parametres-batico/restrictions`, { headers });
      setRestrictions(response.data || []);
    } catch {
      setRestrictions([]);
    }
  };

  useEffect(() => {
    fetchRestrictions();
  }, []);

  const openRestrictionDialog = (restriction = null) => {
    if (restriction) {
      setEditingRestriction(restriction);
      // Convertir la valeur en liste de territoires si c'est un type territoire
      const territoiresSelectionnes = restriction.type === 'territoire' && restriction.valeur 
        ? restriction.valeur.split(',').map(t => t.trim()).filter(t => t)
        : [];
      setRestrictionForm({
        nom: restriction.nom || '',
        type: restriction.type || 'territoire',
        condition: restriction.condition || 'equals',
        valeur: restriction.valeur || '',
        territoires_selectionnes: territoiresSelectionnes,
        action: restriction.action || 'bloquer',
        action_config: restriction.action_config || { pourcentage: 0 },
        message: restriction.message || '',
        message_interne: restriction.message_interne || '',
        is_active: restriction.is_active !== false,
        priorite: restriction.priorite || 0,
        applicable_categories: restriction.applicable_categories || ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
        applicable_modes: restriction.applicable_modes || ['annuel', 'nominative']
      });
    } else {
      setEditingRestriction(null);
      setRestrictionForm({
        nom: '',
        type: 'territoire',
        condition: 'equals',
        valeur: '',
        territoires_selectionnes: [],
        action: 'bloquer',
        action_config: { pourcentage: 0 },
        message: '',
        message_interne: '',
        is_active: true,
        priorite: 0,
        applicable_categories: ['gros_oeuvre', 'second_oeuvre', 'intellectuels', 'travaux_publics'],
        applicable_modes: ['annuel', 'nominative']
      });
    }
    setRestrictionDialogOpen(true);
  };

  const handleSaveRestriction = async () => {
    if (!restrictionForm.nom.trim()) {
      toast.error('Le nom de la restriction est obligatoire');
      return;
    }

    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      if (editingRestriction) {
        await axios.put(
          `${BACKEND_URL}/api/parametres-batico/restrictions/${editingRestriction.id}`,
          restrictionForm,
          { headers }
        );
        toast.success('Restriction mise à jour avec succès');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/parametres-batico/restrictions`,
          restrictionForm,
          { headers }
        );
        toast.success('Nouvelle restriction créée avec succès');
      }

      setRestrictionDialogOpen(false);
      fetchRestrictions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteRestriction = async (restrictionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette restriction ?')) {
      return;
    }

    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${BACKEND_URL}/api/parametres-batico/restrictions/${restrictionId}`, { headers });
      toast.success('Restriction supprimée');
      fetchRestrictions();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleRestriction = async (restriction) => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${BACKEND_URL}/api/parametres-batico/restrictions/${restriction.id}`,
        { ...restriction, is_active: !restriction.is_active },
        { headers }
      );
      toast.success(`Restriction ${restriction.is_active ? 'désactivée' : 'activée'}`);
      fetchRestrictions();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getRestrictionTypeInfo = (type) => {
    return RESTRICTION_TYPES.find(t => t.value === type) || RESTRICTION_TYPES[0];
  };

  const getRestrictionActionInfo = (action) => {
    return RESTRICTION_ACTIONS.find(a => a.value === action) || RESTRICTION_ACTIONS[0];
  };

  // Import functions
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        
        if (file.name.endsWith('.json')) {
          const data = JSON.parse(content);
          setImportData(data);
          setImportDialogOpen(true);
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n');
          const _headers = lines[0].split(';');
          const data = lines.slice(1).filter(l => l.trim()).map(line => {
            const values = line.split(';');
            return {
              coef_gros_oeuvre: parseFloat(values[2]) || 0,
              coef_second_oeuvre: parseFloat(values[3]) || 0,
              coef_intellectuels: parseFloat(values[4]) || 0,
              coef_travaux_publics: parseFloat(values[5]) || 0,
              remise_gros_oeuvre: parseFloat(values[6]?.replace('%', '')) / 100 || 0,
              remise_second_oeuvre: parseFloat(values[7]?.replace('%', '')) / 100 || 0,
              remise_intellectuels: parseFloat(values[8]?.replace('%', '')) / 100 || 0,
              remise_travaux_publics: parseFloat(values[9]?.replace('%', '')) / 100 || 0,
              seuil_ca: parseFloat(values[10]) || 40000,
              seuil_ca_actif: values[11]?.toLowerCase() === 'oui'
            };
          });
          setImportData({ parametres: data });
          setImportDialogOpen(true);
        } else {
          toast.error('Format de fichier non supporté. Utilisez JSON ou CSV.');
        }
      } catch {
        toast.error('Erreur lors de la lecture du fichier');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const confirmImport = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // If importing from JSON with parametres_actifs
      if (importData.parametres_actifs) {
        const params = importData.parametres_actifs;
        await axios.post(
          `${BACKEND_URL}/api/parametres-batico`,
          {
            coef_gros_oeuvre: params.coef_gros_oeuvre,
            coef_second_oeuvre: params.coef_second_oeuvre,
            coef_intellectuels: params.coef_intellectuels,
            coef_travaux_publics: params.coef_travaux_publics,
            remise_gros_oeuvre: params.remise_gros_oeuvre,
            remise_second_oeuvre: params.remise_second_oeuvre,
            remise_intellectuels: params.remise_intellectuels,
            remise_travaux_publics: params.remise_travaux_publics,
            seuil_ca: params.seuil_ca,
            seuil_ca_actif: params.seuil_ca_actif
          },
          { headers }
        );
        toast.success('Import JSON réussi !');
      }
      // If importing from CSV array
      else if (importData.parametres && importData.parametres.length > 0) {
        const latestParams = importData.parametres[importData.parametres.length - 1];
        await axios.post(
          `${BACKEND_URL}/api/parametres-batico`,
          latestParams,
          { headers }
        );
        toast.success(`Import réussi ! ${importData.parametres.length} entrée(s) trouvée(s), dernière importée.`);
      }

      setImportDialogOpen(false);
      setImportData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    }
  };

  const handlePreview = (params) => {
    setPreviewParams(params);
    setPreviewDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  return {
    confirmImport, copyToClipboard, fetchRestrictions, formatCustomParamValue, getParamTypeInfo, getRestrictionActionInfo,
    getRestrictionTypeInfo, handleDeleteCustomParam, handleDeleteRestriction, handleFileImport, handlePreview, handleSaveCustomParam,
    handleSaveRestriction, handleToggleCustomParam, handleToggleRestriction, openCustomParamDialog, openRestrictionDialog, toggleApplicableA};
}
